import { useCallback, useState } from 'react';
import * as lifecycleApi from '../../requirement-lifecycle/api';

/**
 * The AI rewrite awaiting the user's decision: the PENDING_REVIEW version it
 * was stored as, and the text it proposes.
 *
 * The two are kept together on purpose. Accept promotes the version by id, and
 * decides whether the user edited it by comparing the text on screen with what
 * was proposed. Tracking the id alone, next to "the last text the fix
 * returned", let them drift apart: a Regenerate that produced no new proposal
 * left the id on the previous proposal while the screen showed other text, and
 * Accept then promoted a rewrite the user was not looking at.
 */
export interface PendingProposal {
  versionId: string;
  text: string;
}

/**
 * Lifecycle of the AI rewrite on the Requirement Doctor's before/after screen.
 *
 * Every operation throws on failure and leaves reporting to the caller, which
 * knows whether to stay on the screen.
 */
export const useRewriteProposal = () => {
  const [pending, setPending] = useState<PendingProposal | undefined>();

  /**
   * Offers `rewrite` for review, replacing whatever was pending. A rewrite
   * identical to the current text is not a proposal — the fix found nothing it
   * could change (TR-01 traceability is the case that surfaced this) — so
   * nothing is stored.
   *
   * The previous proposal is forgotten first, whatever happens next. When no
   * new one is stored — unchanged text, or a failed request — the screen no
   * longer shows the old one, so it must not stay the thing Accept promotes.
   * It is left PENDING_REVIEW on the backend rather than rejected: the user
   * did not decline it. The next edit, approval or deletion retires it, and
   * until then the Lifecycle screen offers it for review.
   */
  const propose = useCallback(
    async (threadId: string, currentText: string, rewrite: string) => {
      setPending(undefined);
      if (rewrite === currentText) return;
      const { instance } = await lifecycleApi.proposeRewrite(threadId, rewrite);
      setPending({ versionId: instance.versionId, text: rewrite });
    },
    [],
  );

  /**
   * Records the refusal of the pending proposal. Without one the AI offered
   * nothing to decline, and this is a no-op.
   */
  const decline = useCallback(
    async (threadId: string) => {
      if (!pending) return;
      await lifecycleApi.rejectInstance(threadId, pending.versionId);
      setPending(undefined);
    },
    [pending],
  );

  /**
   * Saves `finalText` as the requirement, and returns the id of the version
   * that is CURRENT afterwards.
   *
   * With a proposal pending it is accepted; if the user changed its text, the
   * change is saved as the version after it. Collapsing the two would credit
   * the user's wording to the AI, and rejecting the proposal instead would
   * record a refusal the user never made. Without a proposal this is a plain
   * manual edit.
   *
   * `onProposalAccepted` runs as soon as the proposal is CURRENT, before the
   * edit is attempted. The two are separate requests, so the edit can fail
   * after the accept went through; the caller uses this to reflect the
   * accepted version straight away, so the screen and the backend agree even
   * then. A retry finds nothing pending and saves only the edit.
   */
  const accept = useCallback(
    async (
      threadId: string,
      finalText: string,
      onProposalAccepted?: (accepted: PendingProposal) => void,
    ): Promise<string> => {
      if (!pending) {
        return (await lifecycleApi.editInstance(threadId, finalText)).versionId;
      }
      const accepted = await lifecycleApi.acceptInstance(
        threadId,
        pending.versionId,
      );
      setPending(undefined);
      onProposalAccepted?.({
        versionId: accepted.versionId,
        text: pending.text,
      });
      if (finalText === pending.text) {
        return accepted.versionId;
      }
      return (await lifecycleApi.editInstance(threadId, finalText)).versionId;
    },
    [pending],
  );

  /** Forgets the proposal without touching the backend (leaving the page). */
  const clear = useCallback(() => setPending(undefined), []);

  return {
    hasPendingProposal: pending !== undefined,
    propose,
    decline,
    accept,
    clear,
  };
};
